import * as Yup from 'yup';

import {
  EnterpriseRatingSeverityDefault,
  IUnderwritingCoverage,
  IUnderwritingEndorsement,
  IUnderwritingEnterpriseRating,
  IUnderwritingQuote,
  IUnderwritingQuoteExcessLayerParticipant,
  IUnderwritingQuoteExcessTowerLayer,
  IUnderwritingScheduleRating,
  IUnderwritingSubjectivity,
} from './types';
import {
  Exact,
  GetRaterQuery,
  GetRaterQueryVariables,
  InputMaybe,
  MediaEventCoverageOption,
  PaginationRequest,
  QuoteType,
  SignificantTermsConditions,
  SubmissionType,
} from '../gql/graphql';
import {
  QuoteLineItemFeatures,
  QuoteLineItemType,
} from './utils.quote-line-item-features';
import { TypedDocumentNode, useQuery } from '@apollo/client';
import {
  addMetadataToCoverage,
  addMetadataToEndorsements,
  getDisplayNameFromEntity,
  getFirstPaginatedNode,
  getPaginatedNodes,
  sortCoveragesByType,
} from './utils';
import {
  formatListOfObjectValues,
  handleValidationErrors,
  isNullOrUndefined,
} from '@converge/utils';
import { useEffect, useMemo } from 'react';

import config from '@converge/config';
import { graphql } from '../gql';
import { v4 as uuidv4 } from 'uuid';

export const GET_RATER = graphql(`
  query getRater(
    $uuid: String
    $referenceId: String
    $submissionUuid: String
    $pagination: PaginationRequest
    $type: QuoteType
    $entitiesBugHasBeenFixed: Boolean = false
  ) {
    policies(filters: { quoteUuid: $uuid }) {
      edges {
        cursor
        node {
          uuid
          referenceId
        }
      }
    }
    userQuotes: quotes(
      filters: { submissionUuid: $submissionUuid, type: USER_QUOTE }
      sort: { order: "ASC", key: "order" }
    ) {
      pageInfo {
        totalResults
      }
    }
    quotes(
      pagination: $pagination
      filters: {
        uuid: $uuid
        referenceId: $referenceId
        submissionUuid: $submissionUuid
        type: $type
      }
      sort: { order: "ASC", key: "order" }
    ) {
      edges {
        cursor
        node {
          uuid
          createdAt
          endorsementImpact
          erFusionScore
          erFusionScoreModifier
          erMaxFusionScoreModifier
          erMinFusionScoreModifier
          erRatedFusionScoreModifier
          erSelectedFusionScoreModifier
          isObsolete
          limit
          minimumPremium
          selectedQuotedPremium
          quotedRatedRatio
          modifiedCoveragePremium
          name
          order
          quotedPremium
          ratedPremium
          referenceId
          inForcePolicy
          retention
          srModifier
          srPercentage
          status
          stcMaxModifier
          stcMinModifier
          stcRatedModifier
          stcModifier
          stcSelectedModifier
          technicalPremium
          type
          updatedAt
          coverages(sort: { key: "order", order: "ASC" }) {
            edges {
              cursor
              node {
                uuid
                name
                type
                order
                createdAt
                updatedAt

                quote {
                  basePremium
                  commissionAdjustmentFactor
                  fusionScore
                  fusionScoreModifier
                  selectedFusionScoreModifier
                  ratedFusionScoreModifier
                  enhancedMediaFactor
                  fullPriorActs
                  hazardGroup
                  included
                  indemnityPeriodFactor
                  limit
                  limitRetentionFactor
                  maxFusionScoreModifier
                  minFusionScoreModifier
                  modifiedCoveragePremium
                  policyTermFactor
                  priorActsFactor
                  restorationPeriodDays
                  restorationPeriodFactor
                  retention
                  retroactiveDate
                  sharedCoverageAggregateFactor
                  sharedPolicyAggregateFactor
                  subCoverageFactor
                  technicalPremium

                  uuid
                  waitingPeriodDays
                  waitingPeriodFactor
                  waitingPeriodHours
                  participantCoverageName
                  erosion
                }
                endorsements {
                  edges {
                    cursor
                    node {
                      uuid
                      type
                      name
                      order
                      excess
                      primary
                      description
                      nonPremiumBearing
                      questions {
                        edges {
                          node {
                            isRequired
                            order
                            parent
                            question
                            section
                            sectionUuid
                            subGroupUuid
                            subSection
                            subSectionUuid
                            type
                            uuid
                            groupUuid
                            answer {
                              answer
                              createdAt
                              type
                              updatedAt
                              uuid
                            }
                          }
                        }
                      }
                      quote {
                        basePremium
                        commissionAdjustmentFactor
                        fusionScore
                        fusionScoreModifier
                        enhancedMediaFactor
                        fullPriorActs
                        hazardGroup
                        included
                        includedInExcess
                        indemnityPeriodFactor
                        limit
                        limitRetentionFactor
                        maxFusionScoreModifier
                        mediaEventCoverageOption
                        minFusionScoreModifier
                        modifiedCoveragePremium
                        policyTermFactor
                        priorActsFactor
                        ratedFusionScoreModifier
                        restorationPeriodDays
                        restorationPeriodFactor
                        retention
                        retroactiveDate
                        selectedFusionScoreModifier
                        sharedCoverageAggregateFactor
                        sharedPolicyAggregateFactor
                        subCoverageFactor
                        technicalPremium
                        uuid
                        waitingPeriodFactor
                        waitingPeriodHours
                      }
                      coverageUuid
                      createdAt
                      updatedAt
                    }
                  }
                }

                subCoverages(sort: { key: "order", order: "ASC" }) {
                  edges {
                    cursor
                    node {
                      uuid
                      name
                      order
                      quote {
                        uuid
                        subLimit
                        subRetention
                        included
                        waitingPeriodHours
                        restorationPeriodDays
                        participantCoverageName
                        erosion
                      }
                    }
                  }
                }
              }
            }
          }
          subjectivities {
            edges {
              cursor
              node {
                uuid
                type
                name
                order
                description
                afterPolicy
                priorToPolicy
                priorToBind
                excess
                primary
                quote {
                  uuid
                  included
                  isSatisfied
                  deadline
                  waived
                }
              }
            }
          }
          endorsements {
            edges {
              cursor
              node {
                uuid
                type
                name
                order
                excess
                primary
                description
                nonPremiumBearing
                questions {
                  edges {
                    node {
                      isRequired
                      order
                      parent
                      question
                      section
                      sectionUuid
                      subGroupUuid
                      subSection
                      subSectionUuid
                      type
                      uuid
                      groupUuid
                      answer {
                        answer
                        createdAt
                        type
                        updatedAt
                        uuid
                      }
                    }
                  }
                }
                quote {
                  basePremium
                  commissionAdjustmentFactor
                  fusionScore
                  fusionScoreModifier
                  enhancedMediaFactor
                  fullPriorActs
                  hazardGroup
                  included
                  includedInExcess
                  indemnityPeriodFactor
                  limit
                  limitRetentionFactor
                  maxFusionScoreModifier
                  mediaEventCoverageOption
                  minFusionScoreModifier
                  modifiedCoveragePremium
                  policyTermFactor
                  priorActsFactor
                  ratedFusionScoreModifier
                  restorationPeriodDays
                  restorationPeriodFactor
                  retention
                  retroactiveDate
                  selectedFusionScoreModifier
                  sharedCoverageAggregateFactor
                  sharedPolicyAggregateFactor
                  subCoverageFactor
                  technicalPremium
                  uuid
                  waitingPeriodFactor
                  waitingPeriodHours
                }
                coverageUuid
                createdAt
                updatedAt
              }
            }
          }
          enterpriseRatings(sort: { key: "name", order: "ASC" }) {
            edges {
              cursor
              node {
                uuid
                name
                quote {
                  uuid
                  riskLevel
                  createdAt
                  updatedAt
                }
                createdAt
                updatedAt
              }
            }
          }
          scheduleRatings(sort: { key: "name", order: "ASC" }) {
            edges {
              cursor
              node {
                uuid
                name
                quote {
                  uuid
                  percentage
                  createdAt
                  updatedAt
                }
                excess
                primary
                createdAt
                updatedAt
              }
            }
          }
          excessConvergeAttachmentPoint
          excessConvergeIncreasedLimitFactor
          excessConvergeLimit
          excessConvergePartOfLimit
          excessConvergeQuotaShare
          excessConvergeQuotedPremium
          excessConvergeQuotedToBenchmarkRatio
          excessConvergeRatedPremium
          excessConvergeUnderlyingPremiumPercent
          excessCorrectiveFactor
          excessEndorsementImpact
          excessMaxCorrectiveFactor
          excessMinCorrectiveFactor
          excessConvergeQuotedRatedRatio
          excessSelectedCorrectiveFactor
          obsoleteAt
          srExcessModifier
          srExcessPercentage
          stcExcessMaxModifier
          stcExcessMinModifier
          stcExcessModifier
          stcExcessRatedModifier
          stcExcessSelectedModifier
          excessTowerLayers {
            edges {
              cursor
              queryCursor
              node {
                primary
                attachmentPoint
                benchmarkPremium
                boundIncreasedLimitFactor
                boundPremium
                boundUnderlyingPercent
                companyShare
                createdAt
                limit
                minimumPremium
                name
                position
                updatedAt
                uuid
                participants(filters: { insurer: null, status: ACTIVE }) {
                  edges {
                    cursor
                    queryCursor
                    node {
                      referenceId
                      status
                      updatedAt
                      uuid
                      insurer
                      quote {
                        policyReferenceId
                        attachmentPoint # This maps to the rater's retention input field (only for PRIMARY layer)
                        benchmarkPremium
                        boundIncreasedLimitFactor
                        boundPremium
                        boundUnderlyingPercent
                        companyShare
                        limit
                        minimumPremium
                        uuid
                      }
                      coverages {
                        edges {
                          node {
                            uuid
                            name
                            type
                            order
                            createdAt
                            updatedAt
                            quote {
                              basePremium
                              commissionAdjustmentFactor
                              fusionScore
                              fusionScoreModifier
                              selectedFusionScoreModifier
                              ratedFusionScoreModifier
                              enhancedMediaFactor
                              fullPriorActs
                              hazardGroup
                              included
                              indemnityPeriodFactor
                              limit
                              limitRetentionFactor
                              maxFusionScoreModifier
                              minFusionScoreModifier
                              modifiedCoveragePremium
                              policyTermFactor
                              priorActsFactor
                              restorationPeriodDays
                              restorationPeriodFactor
                              retention
                              retroactiveDate
                              sharedCoverageAggregateFactor
                              sharedPolicyAggregateFactor
                              subCoverageFactor
                              technicalPremium
                              uuid
                              waitingPeriodDays
                              waitingPeriodFactor
                              waitingPeriodHours
                              participantCoverageName
                              erosion
                            }
                            subCoverages(sort: { key: "order", order: "ASC" }) {
                              edges {
                                cursor
                                node {
                                  uuid
                                  name
                                  order
                                  quote {
                                    uuid
                                    subLimit
                                    subRetention
                                    included
                                    waitingPeriodHours
                                    restorationPeriodDays
                                    participantCoverageName
                                    erosion
                                  }
                                }
                              }
                            }
                          }
                        }
                      }

                      aliases {
                        edges {
                          cursor
                          queryCursor
                          node {
                            createdAt
                            name
                            primary
                            sources
                            type
                            updatedAt
                            uuid
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    entities(
      filters: { submissionUuid: $submissionUuid, submissionType: INSURED }
      pagination: $pagination
    ) @include(if: $entitiesBugHasBeenFixed) {
      edges {
        cursor
        node {
          coverages(sort: { key: "order", order: "ASC" }) {
            edges {
              cursor
              node {
                uuid
                name
                type
                score
              }
            }
          }
        }
      }
    }
  }
`);

export enum RaterMode {
  CreateQuote = 'create',
  EditQuote = 'edit',
}

export function useGetRaterQuery({
  uuid,
  type = uuid ? QuoteType.UserQuote : QuoteType.DefaultQuote,
  ...variables
}: GetRaterQueryVariables) {
  const { loading, error, data } = useQuery(
    GET_RATER as TypedDocumentNode<
      GetRaterQuery,
      Exact<{
        uuid?: InputMaybe<string> | undefined;
        referenceId?: InputMaybe<string> | undefined;
        submissionUuid?: InputMaybe<string> | undefined;
        pagination?: InputMaybe<PaginationRequest> | undefined;
        type?: InputMaybe<QuoteType> | undefined;
        entitiesBugHasBeenFixed?: InputMaybe<boolean> | undefined;
        inForcePolicy?: InputMaybe<boolean> | undefined;
      }>
    >,
    {
      variables: {
        uuid,
        type,
        ...variables,
      },
      fetchPolicy: 'no-cache',
    }
  );

  const info = useMemo(() => {
    const fusionScore = getFirstPaginatedNode(
      getFirstPaginatedNode(data?.entities)?.coverages
    )?.score;
    const quote = getFirstPaginatedNode(data?.quotes);
    const policy = getFirstPaginatedNode(data?.policies);
    const userQuotes = data?.userQuotes;

    let underwritingQuote: IUnderwritingQuote =
      quote as unknown as IUnderwritingQuote;
    if (underwritingQuote && quote) {
      underwritingQuote = {
        ...underwritingQuote,
        excessTowerLayers: getPaginatedNodes(quote.excessTowerLayers).map(
          (layer): IUnderwritingQuoteExcessTowerLayer => {
            return {
              ...layer,
              isNew: false,
              participants: getPaginatedNodes(
                layer.participants ?? {
                  edges: [],
                }
              ).map((participant): IUnderwritingQuoteExcessLayerParticipant => {
                return {
                  ...participant,
                  isNew: false,
                  quote: {
                    ...participant.quote,
                    uuid: participant.quote?.uuid ?? uuidv4(),
                    limit: participant.quote?.limit ?? null,
                    policyReferenceId:
                      participant.quote?.policyReferenceId ?? '',
                  },
                  name: getDisplayNameFromEntity(participant) ?? 'N/A',
                  coverages: sortCoveragesByType(
                    getPaginatedNodes(participant.coverages)
                  ).map((coverage) => ({
                    ...coverage,
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    quote: coverage.quote!,
                    subCoverages: getPaginatedNodes(coverage.subCoverages).map(
                      (subCoverage) => ({
                        ...subCoverage,
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        quote: subCoverage.quote!,
                      })
                    ),
                  })),
                };
              }),
            };
          }
        ),
        fusionScore,
        scheduleRatings: getPaginatedNodes(
          quote.scheduleRatings
        ) as IUnderwritingScheduleRating[],
        enterpriseRatings: (
          getPaginatedNodes(
            quote.enterpriseRatings
          ) as IUnderwritingEnterpriseRating[]
        ).map((rating) => ({
          ...rating,
          quote: {
            ...rating.quote,
            riskLevel:
              rating.quote.riskLevel ?? EnterpriseRatingSeverityDefault,
          },
        })),
        endorsementImpact:
          quote.endorsementImpact ?? SignificantTermsConditions.Neutral,
        subjectivities:
          (quote?.subjectivities.edges
            .filter((subjectivity) => subjectivity.node.uuid)
            .map(
              (subjectivity) => subjectivity.node
            ) as IUnderwritingSubjectivity[]) ?? [],
        coverages:
          sortCoveragesByType([
            ...getPaginatedNodes(quote?.coverages).map((coverage) => {
              return {
                ...coverage,
                subCoverages: getPaginatedNodes(coverage.subCoverages),
                endorsements: getPaginatedNodes(coverage.endorsements),
              };
            }),
          ] as unknown as IUnderwritingCoverage[]).map((coverage) => {
            return addMetadataToCoverage(coverage);
          }) ?? [],
        endorsements: (
          (quote?.endorsements.edges
            .filter((endorsement) => endorsement.node.uuid)
            .map(
              (endorsement) => endorsement.node
            ) as IUnderwritingEndorsement[]) ?? []
        ).map((endorsement) => {
          const endorsementWithMetadata =
            addMetadataToEndorsements(endorsement);

          if (!endorsementWithMetadata.quote.fusionScore) {
            endorsementWithMetadata.quote = {
              ...endorsementWithMetadata.quote,
              fusionScore: fusionScore,
            };
          }

          return endorsementWithMetadata;
        }),
      };
    }

    return {
      policy,
      quote,
      userQuotes,
      underwritingQuote,
    };
  }, [data]);

  useEffect(() => {
    if (!config.featureFlags.enableRaterQueryValidation) {
      return;
    }
    const dataToValidate = {
      variables: {
        uuid,
        type,
        ...variables,
      },
      data,
      loading,
      error,
    };
    try {
      RATER_QUERY_SCHEMA().validateSync(dataToValidate, {
        abortEarly: false,
        // strict: true,
      });
    } catch (cause) {
      handleValidationErrors({
        cause,
        source: 'useGetRaterQuery',
        inputData: dataToValidate,
        message: 'Invalid rater query state',
        throwError: false,
      });
    }
  }, [data, error, loading, type, uuid, variables]);

  return {
    error,
    loading,
    data,
    info,
  };
}

const VARIABLES_SCHEMA = Yup.object().shape({
  uuid: Yup.string(),
  referenceId: Yup.string(),
  submissionUuid: Yup.string().required(),
  pagination: Yup.object().shape({
    first: Yup.number(),
    after: Yup.string(),
  }),
  type: Yup.string().required(),
});

const PARTICIPANT_ENTITY_ALIAS_SCHEMA = Yup.object()
  .shape({
    edges: Yup.array()
      .of(
        Yup.object()
          .shape({
            cursor: Yup.number().required(),
            queryCursor: Yup.string().required(),
            node: Yup.object()
              .shape({
                createdAt: Yup.string().required(),
                name: Yup.string().required(),
                primary: Yup.boolean().required(),
                sources: Yup.array().of(Yup.string().required()).required(),
                type: Yup.string().required(),
                updatedAt: Yup.string().required(),
                uuid: Yup.string().required(),
              })
              .required(),
          })
          .required()
      )
      .required(),
  })
  .required();

const PRIMARY_PARTICIPANT_SCHEMA = Yup.object()
  .shape({
    cursor: Yup.number().required(),
    queryCursor: Yup.string().required(),
    node: Yup.object()
      .shape({
        referenceId: Yup.string().required(),
        status: Yup.string().required(),
        updatedAt: Yup.string().required(),
        uuid: Yup.string().required(),
        insurer: Yup.string().nullable(),
        quote: Yup.object().shape({
          attachmentPoint: Yup.number().min(0.01).required(),
          benchmarkPremium: Yup.number().nullable(),
          boundIncreasedLimitFactor: Yup.number().nullable(),
          boundPremium: Yup.number().required(),
          boundUnderlyingPercent: Yup.number().nullable(),
          companyShare: Yup.number().required(),
          limit: Yup.number().min(1).required(),
          minimumPremium: Yup.number().nullable().oneOf([null]),
          uuid: Yup.string().required(),
        }),
        aliases: PARTICIPANT_ENTITY_ALIAS_SCHEMA,
      })
      .required(),
  })
  .required();

const EXCESS_PARTICIPANT_SCHEMA = Yup.object()
  .shape({
    cursor: Yup.number().required(),
    queryCursor: Yup.string().required(),
    node: Yup.object()
      .shape({
        referenceId: Yup.string().required(),
        status: Yup.string().required(),
        updatedAt: Yup.string().required(),
        uuid: Yup.string().required(),
        insurer: Yup.string().nullable(),
        quote: Yup.object().shape({
          attachmentPoint: Yup.number().min(1).required(),
          benchmarkPremium: Yup.number().min(1).required(),
          boundIncreasedLimitFactor: Yup.number().min(1).required(),
          boundPremium: Yup.number()
            .min(1)
            .when('limit', {
              is: null,
              then: (schema) => schema.nullable(),
              otherwise: (schema) => schema.required(),
            }),
          boundUnderlyingPercent: Yup.number()
            .min(0)
            .max(1)
            .when('limit', {
              is: null,
              then: (schema) => schema.nullable(),
              otherwise: (schema) => schema.required(),
            }),
          companyShare: Yup.number()
            .min(0)
            .max(1)
            .when('limit', {
              is: null,
              then: (schema) => schema.nullable(),
              otherwise: (schema) => schema.required(),
            }),
          limit: Yup.number().min(1).nullable(),
          minimumPremium: Yup.number()
            .min(1)
            .when('limit', {
              is: null,
              then: (schema) => schema.nullable(),
              otherwise: (schema) =>
                schema.required('Minimum premium is required if limit is set'),
            }),
          uuid: Yup.string().required(),
        }),
        aliases: PARTICIPANT_ENTITY_ALIAS_SCHEMA,
      })
      .required(),
  })
  .required();

const ENDORSEMENT_QUOTE_SCHEMA = (policyType?: SubmissionType | null) =>
  Yup.object()
    .shape({
      basePremium: Yup.number().required(),
      commissionAdjustmentFactor: Yup.number().required(),
      enhancedMediaFactor: Yup.number().required(),
      fullPriorActs: Yup.boolean().nullable(),
      hazardGroup: Yup.number().min(0).max(9).nullable(),
      included: Yup.boolean().required(),
      includedInExcess:
        policyType === SubmissionType.Excess
          ? Yup.boolean().required()
          : Yup.boolean().nullable(),
      // TODO: Should be required?
      indemnityPeriodFactor: Yup.number().nullable(),
      limit: Yup.number().required(),
      limitRetentionFactor: Yup.number().required(),
      maxFusionScoreModifier: Yup.number().required(),
      mediaEventCoverageOption: Yup.string()
        .nullable()
        .test('media-event-coverage-option', (value, context) => {
          const [, endorsement] = context.from ?? [];

          if (!endorsement) {
            return context.createError({
              message: `Unable to get endorsement from ${context.path}`,
            });
          }

          const features = QuoteLineItemFeatures.from(
            QuoteLineItemType.Endorsement,
            endorsement.value
          ).toJSON();

          if (!features.enableMediaEventCoverage) {
            if (!isNullOrUndefined(value)) {
              return context.createError({
                message: `Media event coverage option (${value}) is not applicable for this endorsement`,
              });
            }
            return true;
          }

          if (
            !Object.values(MediaEventCoverageOption).includes(
              value as MediaEventCoverageOption
            )
          ) {
            return context.createError({
              message: `Media event coverage option must be ${formatListOfObjectValues(
                MediaEventCoverageOption,
                'or'
              )} for this endorsement`,
            });
          }

          return true;
        }),
      minFusionScoreModifier: Yup.number().required(),
      modifiedCoveragePremium: Yup.number().required(),
      policyTermFactor: Yup.number().required(),
      priorActsFactor: Yup.number().required(),
      ratedFusionScoreModifier: Yup.number().required(),
      restorationPeriodDays: Yup.number().nullable(),
      restorationPeriodFactor: Yup.number().required(),
      retention: Yup.number().required(),
      retroactiveDate: Yup.date().nullable(),
      selectedFusionScoreModifier: Yup.number().nullable(),
      sharedCoverageAggregateFactor: Yup.number().required(),
      sharedPolicyAggregateFactor: Yup.number().required(),
      subCoverageFactor: Yup.number().required(),
      technicalPremium: Yup.number().required(),
      uuid: Yup.string().required(),
      waitingPeriodFactor: Yup.number().nullable(),
      waitingPeriodHours: Yup.number().nullable(),
    })
    .required();

const PREMIUM_BEARING_ENDORSEMENT_QUOTE_SCHEMA = (
  policyType?: SubmissionType | null
) =>
  ENDORSEMENT_QUOTE_SCHEMA(policyType).concat(
    Yup.object().shape({
      fusionScore: Yup.number().required(),
      fusionScoreModifier: Yup.number().required(),
    })
  );

const NON_PREMIUM_BEARING_ENDORSEMENT_QUOTE_SCHEMA = (
  policyType?: SubmissionType | null
) =>
  ENDORSEMENT_QUOTE_SCHEMA(policyType).concat(
    Yup.object().shape({
      fusionScore: Yup.number()
        .nullable()
        .test(
          'is-null',
          'Non-premium bearing endorsements should not have a fusion score',
          (value) => value === null
        ),
      fusionScoreModifier: Yup.number()
        .nullable()
        .test(
          'is-null',
          'Non-premium bearing endorsements should not have a fusion score modifier',
          (value) => value === null
        ),
    })
  );

const DATA_SCHEMA = (policyType?: SubmissionType | null) =>
  Yup.object().shape({
    // policies: Yup.object().shape({
    //   edges: Yup.array().of(
    //     Yup.object().shape({
    //       cursor: Yup.number().required(),
    //       node: Yup.object().shape({
    //         uuid: Yup.string().required(),
    //         referenceId: Yup.string().required(),
    //       }),
    //     })
    //   ),
    // }),
    // userQuotes: Yup.object().shape({
    //   pageInfo: Yup.object().shape({
    //     totalResults: Yup.number().required(),
    //   }),
    // }),
    quotes: Yup.object()
      .shape({
        edges: Yup.array()
          .of(
            Yup.object()
              .shape({
                cursor: Yup.number().required(),
                node: Yup.object()
                  .shape({
                    uuid: Yup.string().required(),
                    createdAt: Yup.string().required(),
                    endorsementImpact: Yup.string().required(),
                    erFusionScore: Yup.number().nullable(),
                    erFusionScoreModifier: Yup.number().required(),
                    erMaxFusionScoreModifier: Yup.number().required(),
                    erMinFusionScoreModifier: Yup.number().required(),
                    erRatedFusionScoreModifier: Yup.number().required(),
                    erSelectedFusionScoreModifier: Yup.number().nullable(),
                    // TODO: Should not need default value:
                    isObsolete: Yup.boolean().default(false).nullable(),
                    limit: Yup.number().required(),
                    minimumPremium: Yup.number().required(),
                    modifiedCoveragePremium: Yup.number().required(),
                    name: Yup.string().required(),
                    order: Yup.number().required(),
                    quotedPremium: Yup.number().required(),
                    selectedQuotedPremium: Yup.number(),
                    quotedRatedRatio: Yup.number().required(),
                    ratedPremium: Yup.number().required(),
                    referenceId: Yup.string().required(),
                    retention: Yup.number().required(),
                    srModifier: Yup.number().required(),
                    srPercentage: Yup.number().required(),
                    status: Yup.string().required(),
                    stcMaxModifier: Yup.number().required(),
                    stcMinModifier: Yup.number().required(),
                    stcRatedModifier: Yup.number().required(),
                    stcModifier: Yup.number().required(),
                    stcSelectedModifier: Yup.number().nullable(),
                    technicalPremium: Yup.number().required(),
                    type: Yup.string().required(),
                    updatedAt: Yup.string().required(),
                    excessTowerLayers: Yup.object()
                      .shape({
                        edges:
                          policyType === SubmissionType.Primary
                            ? Yup.array().length(
                                0,
                                'Quotes for a primary policy submission should not have excess tower layers'
                              )
                            : Yup.array()
                                .of(
                                  Yup.object()
                                    .shape({
                                      cursor: Yup.number().required(),
                                      queryCursor: Yup.string().required(),
                                      node: Yup.object()
                                        .shape({
                                          position: Yup.number()
                                            .min(0)
                                            .test(
                                              'position-index-mismatch',
                                              'Position',
                                              (value, context) => {
                                                // const [node, edge, edges] =
                                                //   context.from ?? [];

                                                // console.error(
                                                //   node,
                                                //   edge,
                                                //   edges
                                                // );

                                                const indexMatch =
                                                  context.path.match(
                                                    /\[(\d+)\]/gm
                                                  ) ?? [];

                                                let index = indexMatch.pop();

                                                if (!indexMatch) {
                                                  return context.createError({
                                                    message: `Unable to get index from ${context.path}`,
                                                  });
                                                }

                                                index = index?.slice(
                                                  1,
                                                  index.length - 1
                                                );

                                                if (`${index}` !== `${value}`) {
                                                  return context.createError({
                                                    message: `Position index mismatch. Expected ${index}, received ${value}`,
                                                  });
                                                }

                                                return true;
                                              }
                                            )
                                            .required(),
                                          // name: Yup.string().required(),
                                          uuid: Yup.string().required(),
                                          primary: Yup.boolean()
                                            .when('position', {
                                              is: 0,
                                              then: (schema) =>
                                                schema.oneOf([true]),
                                              otherwise: (schema) =>
                                                schema.oneOf([false]),
                                            })
                                            .required(),
                                          attachmentPoint: Yup.number()
                                            .min(1)
                                            .required(),
                                          benchmarkPremium: Yup.number()
                                            .min(1)
                                            .required(),
                                          boundPremium: Yup.number().required(),
                                          boundIncreasedLimitFactor:
                                            Yup.number()
                                              .min(0)
                                              .when('position', {
                                                is: 0,
                                                then: (schema) =>
                                                  schema.nullable(),
                                                otherwise: (schema) =>
                                                  schema.required(),
                                              }),
                                          boundUnderlyingPercent:
                                            Yup.number().when('position', {
                                              is: 0,
                                              then: (schema) =>
                                                schema.nullable(),
                                              otherwise: (schema) =>
                                                schema.required(),
                                            }),
                                          companyShare: Yup.number()
                                            .min(0)
                                            .max(1)
                                            .required(),
                                          createdAt: Yup.date().required(),
                                          limit: Yup.number().min(1).nullable(),
                                          minimumPremium: Yup.number()
                                            .min(0)
                                            .when('position', {
                                              is: 0,
                                              then: (schema) =>
                                                schema.nullable(),
                                              otherwise: (schema) =>
                                                schema.when('limit', {
                                                  is: null,
                                                  otherwise: (schema) =>
                                                    schema.required(
                                                      'Minimum premium is required if limit is set'
                                                    ),
                                                }),
                                            }),
                                          updatedAt: Yup.date().required(),
                                          participants: Yup.object().when(
                                            'position',
                                            {
                                              is: 0,
                                              then: (schema) =>
                                                schema
                                                  .shape({
                                                    edges: Yup.array()
                                                      .of(
                                                        PRIMARY_PARTICIPANT_SCHEMA
                                                      )
                                                      .required(),
                                                  })
                                                  .required(),
                                              otherwise: (schema) =>
                                                schema
                                                  .shape({
                                                    edges: Yup.array()
                                                      .of(
                                                        EXCESS_PARTICIPANT_SCHEMA
                                                      )
                                                      .required(),
                                                  })
                                                  .required(),
                                            }
                                          ),
                                        })
                                        .required(),
                                    })
                                    .required()
                                )
                                .required(),
                      })
                      .required(),
                    coverages: Yup.object().shape({
                      edges: Yup.array().of(
                        Yup.object().shape({
                          cursor: Yup.number().required(),
                          node: Yup.object().shape({
                            uuid: Yup.string().required(),
                            name: Yup.string().required(),
                            type: Yup.string().required(),
                            order: Yup.number().required(),
                            createdAt: Yup.string().required(),
                            updatedAt: Yup.string().required(),
                            subCoverages: Yup.object().shape({
                              edges: Yup.array().of(
                                Yup.object().shape({
                                  cursor: Yup.number().required(),
                                  node: Yup.object().shape({
                                    uuid: Yup.string().required(),
                                    name: Yup.string().required(),
                                    order: Yup.number().required(),
                                    quote: Yup.object().shape({
                                      uuid: Yup.string().required(),
                                      subLimit: Yup.number().required(),
                                      subRetention: Yup.number().required(),
                                      included: Yup.boolean().required(),
                                      waitingPeriodHours:
                                        Yup.number().nullable(),
                                      restorationPeriodDays:
                                        Yup.number().nullable(),
                                    }),
                                  }),
                                })
                              ),
                            }),

                            quote: Yup.object().shape({
                              basePremium: Yup.number().required(),
                              commissionAdjustmentFactor:
                                Yup.number().required(),
                              fusionScore: Yup.number().required(),
                              fusionScoreModifier: Yup.number().required(),
                              selectedFusionScoreModifier:
                                Yup.number().nullable(),
                              ratedFusionScoreModifier: Yup.number().required(),
                              enhancedMediaFactor: Yup.number().required(),
                              fullPriorActs: Yup.boolean().nullable(),
                              hazardGroup: Yup.number()
                                .min(0)
                                .max(9)
                                .required(),
                              included: Yup.boolean().required(),
                              // TODO: Should be required?
                              indemnityPeriodFactor: Yup.number().nullable(),
                              limit: Yup.number().required(),
                              limitRetentionFactor: Yup.number().required(),
                              maxFusionScoreModifier: Yup.number().required(),
                              minFusionScoreModifier: Yup.number().required(),
                              modifiedCoveragePremium: Yup.number().required(),
                              policyTermFactor: Yup.number().required(),
                              priorActsFactor: Yup.number().required(),
                              // TODO: Validate based on uuid
                              restorationPeriodDays: Yup.number().nullable(),
                              restorationPeriodFactor: Yup.number().required(),
                              retention: Yup.number().required(),
                              retroactiveDate: Yup.date().required(),
                              sharedCoverageAggregateFactor:
                                Yup.number().required(),
                              sharedPolicyAggregateFactor:
                                Yup.number().required(),
                              subCoverageFactor: Yup.number().required(),
                              technicalPremium: Yup.number().required(),
                              uuid: Yup.string().required(),
                              waitingPeriodDays: Yup.number().nullable(),
                              waitingPeriodFactor: Yup.number().required(),
                              waitingPeriodHours: Yup.number().nullable(),
                            }),
                          }),
                        })
                      ),
                    }),
                    subjectivities: Yup.object().shape({
                      edges: Yup.array().of(
                        Yup.object().shape({
                          cursor: Yup.number().required(),
                          node: Yup.object().shape({
                            uuid: Yup.string().required(),
                            type: Yup.string().required(),
                            name: Yup.string().required(),
                            order: Yup.number().required(),
                            description: Yup.string().nullable(),
                            afterPolicy: Yup.boolean().required(),
                            priorToPolicy: Yup.boolean().required(),
                            priorToBind: Yup.boolean().required(),
                            excess: Yup.boolean().required(),
                            primary: Yup.boolean().required(),
                            quote: Yup.object().shape({
                              uuid: Yup.string().required(),
                              included: Yup.boolean().required(),
                              isSatisfied: Yup.boolean().required(),
                              deadline: Yup.string().required(),
                              waived: Yup.boolean().required(),
                            }),
                          }),
                        })
                      ),
                    }),
                    endorsements: Yup.object().shape({
                      edges: Yup.array().of(
                        Yup.object().shape({
                          cursor: Yup.number().required(),
                          node: Yup.object().shape({
                            uuid: Yup.string().required(),
                            type: Yup.string().required(),
                            name: Yup.string().required(),
                            order: Yup.number().required(),
                            excess: Yup.boolean().required(),
                            primary: Yup.boolean().required(),
                            description: Yup.string().nullable(),
                            nonPremiumBearing: Yup.boolean().required(),
                            quote: Yup.object().when('nonPremiumBearing', {
                              is: true,
                              then: () =>
                                NON_PREMIUM_BEARING_ENDORSEMENT_QUOTE_SCHEMA(
                                  policyType
                                ),
                              otherwise: () =>
                                PREMIUM_BEARING_ENDORSEMENT_QUOTE_SCHEMA(
                                  policyType
                                ),
                            }),
                            createdAt: Yup.string().required(),
                            updatedAt: Yup.string().required(),
                          }),
                        })
                      ),
                    }),
                    enterpriseRatings: Yup.object().shape({
                      edges: Yup.array().of(
                        Yup.object().shape({
                          cursor: Yup.number().required(),
                          node: Yup.object().shape({
                            uuid: Yup.string().required(),
                            name: Yup.string().required(),
                            quote: Yup.object().shape({
                              uuid: Yup.string().required(),
                              riskLevel: Yup.string().required(),
                              createdAt: Yup.string().required(),
                              updatedAt: Yup.string().required(),
                            }),
                            createdAt: Yup.string().required(),
                            updatedAt: Yup.string().required(),
                          }),
                        })
                      ),
                    }),
                    scheduleRatings: Yup.object().shape({
                      edges: Yup.array().of(
                        Yup.object().shape({
                          cursor: Yup.number().required(),
                          node: Yup.object().shape({
                            uuid: Yup.string().required(),
                            name: Yup.string().required(),
                            quote: Yup.object().shape({
                              uuid: Yup.string().required(),
                              percentage: Yup.number().required(),
                              createdAt: Yup.string().required(),
                              updatedAt: Yup.string().required(),
                            }),
                            excess: Yup.boolean().required(),
                            primary: Yup.boolean().required(),
                            createdAt: Yup.string().required(),
                            updatedAt: Yup.string().required(),
                          }),
                        })
                      ),
                    }),
                    excessConvergeAttachmentPoint: Yup.number().nullable(),
                    excessConvergeIncreasedLimitFactor: Yup.number().nullable(),
                    excessConvergeLimit: Yup.number().nullable(),
                    excessConvergePartOfLimit: Yup.number().nullable(),
                    excessConvergeQuotaShare: Yup.number().nullable(),
                    excessConvergeQuotedPremium: Yup.number().nullable(),
                    excessConvergeQuotedToBenchmarkRatio:
                      Yup.number().nullable(),
                    excessConvergeRatedPremium: Yup.number().nullable(),
                    excessConvergeUnderlyingPremiumPercent:
                      Yup.number().nullable(),
                    excessCorrectiveFactor: Yup.number().nullable(),
                    excessEndorsementImpact: Yup.string().nullable(),
                    excessMaxCorrectiveFactor: Yup.number().nullable(),
                    excessMinCorrectiveFactor: Yup.number().nullable(),
                    excessConvergeQuotedRatedRatio: Yup.number().nullable(),
                    excessSelectedCorrectiveFactor: Yup.number().nullable(),
                    // TODO: Improve this to make required when isObsolete is true
                    obsoleteAt: Yup.string().nullable(),
                    srExcessModifier: Yup.number().nullable(),
                    srExcessPercentage: Yup.number().nullable(),
                    stcExcessMaxModifier: Yup.number().nullable(),
                    stcExcessMinModifier: Yup.number().nullable(),
                    stcExcessModifier: Yup.number().nullable(),
                    stcExcessRatedModifier: Yup.number().nullable(),
                    stcExcessSelectedModifier: Yup.number().nullable(),
                  })
                  .required(),
              })
              .required()
          )
          .required(),
      })
      .required(),
    // submissions: Yup.object().shape({
    //   pageInfo: Yup.object().shape({
    //     totalResults: Yup.number().required(),
    //     pageNumber: Yup.number().required(),
    //     pageSize: Yup.number().required(),
    //     hasNextPage: Yup.boolean().required(),
    //     hasPreviousPage: Yup.boolean().required(),
    //   }),
    //   edges: Yup.array().of(
    //     Yup.object().shape({
    //       cursor: Yup.number().required(),
    //       node: Yup.object().shape({
    //         uuid: Yup.string().required(),
    //         referenceId: Yup.string().required(),
    //         type: Yup.string().required(),
    //         status: Yup.string().required(),
    //         aggregateLimit: Yup.number().required(),
    //         policyRetention: Yup.number().required(),
    //         policyStartDate: Yup.string().required(),
    //         policyEndDate: Yup.string().required(),
    //         brokerCommission: Yup.number().required(),
    //         insuredEntity: Yup.object().shape({
    //           cursor: Yup.number().required(),
    //           node: Yup.object().shape({
    //             uuid: Yup.string().required(),
    //             referenceId: Yup.string().required(),
    //             fundamentals: Yup.object().shape({
    //               edges: Yup.array().of(
    //                 Yup.object().shape({
    //                   cursor: Yup.number().required(),
    //                   node: Yup.object().shape({
    //                     uuid: Yup.string().required(),
    //                     revenue: Yup.number().required(),
    //                     primary: Yup.boolean().required(),
    //                     sources: Yup.array().of(Yup.string().required()),
    //                   }),
    //                 })
    //               ),
    //             }),
    //             industrySectors: Yup.object().shape({
    //               edges: Yup.array().of(
    //                 Yup.object().shape({
    //                   cursor: Yup.number().required(),
    //                   node: Yup.object().shape({
    //                     naicsCode: Yup.string().required(),
    //                     uuid: Yup.string().required(),
    //                     sources: Yup.array().of(Yup.string().required()),
    //                     primary: Yup.boolean().required(),
    //                   }),
    //                 })
    //               ),
    //             }),
    //           }),
    //         }),
    //       }),
    //     })
    //   ),
    // }),
    // entities: Yup.object().shape({
    //   edges: Yup.array().of(
    //     Yup.object().shape({
    //       cursor: Yup.number().required(),
    //       node: Yup.object().shape({
    //         coverages: Yup.object().shape({
    //           edges: Yup.array().of(
    //             Yup.object().shape({
    //               cursor: Yup.number().required(),
    //               node: Yup.object().shape({
    //                 uuid: Yup.string().required(),
    //                 name: Yup.string().required(),
    //                 type: Yup.string().required(),
    //                 score: Yup.number().required(),
    //               }),
    //             })
    //           ),
    //         }),
    //       }),
    //     })
    //   ),
    // }),
  });

const RATER_QUERY_SCHEMA = (policyType?: SubmissionType | null) =>
  Yup.object().shape({
    loading: Yup.boolean().required(),
    error: Yup.object().nullable(),
    variables: VARIABLES_SCHEMA,
    data: DATA_SCHEMA(policyType).default(undefined).optional().notRequired(),
  });
